// Local storage backed HTTP cache with TTL
// Strict TypeScript, no any types
export interface CacheEntry<T> {
	readonly timestampMs: number;
	readonly data: T;
}

export const DEFAULT_MAX_AGE_MS = 12 * 60 * 60 * 1000; // 12 hours
const CACHE_PREFIX = 'http-cache::';

const isObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const tryReadLocalStorage = (key: string): string | null => {
	try {
		return localStorage.getItem(key);
	} catch {
		return null;
	}
};

const tryWriteLocalStorage = (key: string, value: string): void => {
	try {
		localStorage.setItem(key, value);
	} catch {
		// ignore quota or access errors
	}
};

const tryRemoveLocalStorage = (key: string): void => {
	try {
		localStorage.removeItem(key);
	} catch {
		// ignore
	}
};

// Stable stringify for params to ensure consistent cache keys
const stableStringify = (value: unknown): string => {
	if (Array.isArray(value)) {
		return `[${value.map((item) => stableStringify(item)).join(',')}]`;
	}
	if (isObject(value)) {
		const keys = Object.keys(value).sort();
		const entries = keys.map((k) => `${JSON.stringify(k)}:${stableStringify((value as Record<string, unknown>)[k])}`);
		return `{${entries.join(',')}}`;
	}
	return JSON.stringify(value);
};

export const buildCacheKey = (url: string, params?: unknown): string => {
	const paramsPart = params === undefined ? '' : `?${stableStringify(params)}`;
	return `${CACHE_PREFIX}${url}${paramsPart}`;
};

export const readFromCache = <T>(key: string, maxAgeMs: number = DEFAULT_MAX_AGE_MS): T | null => {
	const raw = tryReadLocalStorage(key);
	if (!raw) return null;
	try {
		const parsed = JSON.parse(raw) as CacheEntry<unknown>;
		if (typeof parsed.timestampMs !== 'number') {
			tryRemoveLocalStorage(key);
			return null;
		}
		const age = Date.now() - parsed.timestampMs;
		if (age > maxAgeMs) {
			tryRemoveLocalStorage(key);
			return null;
		}
		return parsed.data as T;
	} catch {
		tryRemoveLocalStorage(key);
		return null;
	}
};

export const writeToCache = <T>(key: string, data: T): void => {
	const entry: CacheEntry<T> = { timestampMs: Date.now(), data };
	tryWriteLocalStorage(key, JSON.stringify(entry));
};

export const invalidateCache = (key: string): void => {
	tryRemoveLocalStorage(key);
};

export const invalidateByUrl = (url: string, params?: unknown): void => {
	invalidateCache(buildCacheKey(url, params));
};


